import React, { useState } from 'react'
import Button from '@atlaskit/button'
import LabelIcon from '@atlaskit/icon/glyph/label'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import SettingsIcon from '@atlaskit/icon/glyph/settings'
import { useNotification } from '../../contexts/NotificationContext'
import { clearUserCache } from '../../utils/userCache'
import { LabelsManagement } from './LabelsManagement'

/**
 * SETTINGS NAVIGATION ITEMS
 *
 * Each item defines a settings section with an id, label, icon, and content.
 * To add a new settings page, add a new entry to this array.
 * The first item is selected by default.
 */
const SETTINGS_SECTIONS = [
  {
    id: 'labels',
    label: 'Labels',
    icon: <LabelIcon label="Labels" size="small" />
  },
  {
    id: 'clear-cache',
    label: 'Clear Cache',
    icon: <TrashIcon label="Clear Cache" size="small" />
  }
]

/**
 * SETTINGS PAGE
 *
 * Two-panel layout with a left sidebar for navigation and a right panel
 * for the selected setting's dedicated content area.
 * Follows the same page structure as FolderBrowser: white header + blue bg + white two-panel card.
 *
 * Layout pattern matches Jira's Project Settings pages for a native Jira look and feel.
 * Sidebar is always expanded (no collapse toggle).
 */
function Settings() {
  // Track which settings section is currently active
  const [activeSection, setActiveSection] = useState(SETTINGS_SECTIONS[0].id)

  // Access notification system for success/error feedback after clearing cache
  const { showSuccess, showError } = useNotification()

  /**
   * HANDLE CLEAR CACHE
   *
   * Clears all app localStorage data and in-memory caches.
   * Uses localStorage.clear() since this Jira add-on runs in its own
   * iframe with isolated storage scope — it only affects add-on data.
   * Also clears the in-memory user cache Map via clearUserCache().
   * Reloads the page after a short delay so all React state resets.
   */
  const handleClearCache = () => {
    try {
      // Clear the in-memory user cache (Map + pendingRequests)
      clearUserCache()

      // Clear ALL localStorage for this add-on's iframe origin
      // Covers: sidebar-collapsed, test-expansion-state, expanded-folders-*,
      // expanded-library-folders-*, jira_user_cache, and any other app-created keys
      localStorage.clear()

      // Clear sessionStorage (per-tab state: selected-project-key, current-route)
      sessionStorage.clear()

      // Show success notification (Jira-style auto-dismiss flag)
      showSuccess('Cache Cleared', 'All cached data has been cleared. Reloading...')

      // Reload page after brief delay to let user see the notification
      // This resets all in-memory state (Redux, component state, useLocalStorage hooks)
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error) {
      console.error('Failed to clear cache:', error)
      showError('Clear Cache Failed', 'Could not clear cached data. Please try again.')
    }
  }

  return (
    <div className="settings-page">
      {/* Page Header - white bar with title */}
      <div className="settings-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingsIcon label="Settings" size="medium" primaryColor="var(--ds-text-subtlest, #6B778C)" />
          <h1 className="settings-page-title">Settings</h1>
        </div>
      </div>

      {/* Content area - blue-tinted scrollable background */}
      <div className="settings-content">
        {/* Two-panel card: sidebar + content panel */}
        <div className="settings-panel-container">

          {/* Left Sidebar - navigation items for each setting */}
          <div className="settings-sidebar">
            {SETTINGS_SECTIONS.map((section) => (
              <div
                key={section.id}
                className={`settings-sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                {section.icon}
                <span>{section.label}</span>
              </div>
            ))}
          </div>

          {/* Right Panel - dedicated content area for the selected setting */}
          <div className="settings-panel">
            {/* Labels management - full CRUD for project labels */}
            {activeSection === 'labels' && <LabelsManagement />}

            {activeSection === 'clear-cache' && (
              <div className="settings-panel-content">
                <h2 className="settings-panel-title">Clear Cache</h2>
                <p className="settings-panel-description">
                  Clear locally cached data and force a fresh sync from the server.
                </p>
                {/* Info box listing what cached data will be removed */}
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                    borderRadius: '4px',
                    border: '1px solid var(--ds-border, #DFE1E6)',
                    fontSize: '13px',
                    color: 'var(--ds-text-subtle, #44546F)',
                    lineHeight: '1.6'
                  }}
                >
                  This will reset your personalized settings, including:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Your current project selection</li>
                    <li>Which folders and test cases you had open</li>
                    <li>Saved user information</li>
                    <li>Layout and navigation preferences</li>
                  </ul>
                </div>

                {/* Clear cache action button */}
                <div style={{ marginTop: '20px' }}>
                  <Button
                    appearance="warning"
                    onClick={handleClearCache}
                  >
                    Clear All Cache
                  </Button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

export { Settings }
